import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaTimes, FaSave } from 'react-icons/fa';
import { successAlert, errorAlert } from '../utils/Alerts';
import { useNavigate } from 'react-router-dom';

const ThemeSearcher = ({ token, theme, role, isLoggedIn }) => {
  const [themes,     setThemes]     = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editModal,  setEditModal]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(null); // id del que se está eliminando
  const [form,       setForm]       = useState({ name: '', description: '' });
  const [editForm,   setEditForm]   = useState({ _id: '', name: '', description: '' });

  const isDark = theme === 'dark';

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) handleSearch();
    // eslint-disable-next-line
  }, [isLoggedIn]);

  /* ── BUSCAR ── */
  const handleSearch = async (term = searchTerm) => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/search-themes`, {
        params: { term },
        headers: { Authorization: `Bearer ${token}` },
      });
      setThemes(res.data);
    } catch (err) {
      console.error('Error searching themes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    handleSearch(v);
  };

  /* ── ELIMINAR ── */
  const handleDelete = async (themeId) => {
  if (!window.confirm('¿Eliminar esta temática?')) return;  // ← esto lo dejamos por ahora o puedes usar SweetAlert confirm
  setDeleting(themeId);
  try {
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/admin/themes/${themeId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setThemes(prev => prev.filter(t => t._id !== themeId));
    successAlert({ text: 'Temática eliminada correctamente', timer: 2000 });
  } catch (err) {
    console.error('Error eliminando temática:', err);
    errorAlert({ title: 'Error', text: 'Error al eliminar la temática' });
  } finally {
    setDeleting(null);
  }
};

  /* ── CREAR ── */
    const handleCreateTheme = async () => {
    if (!form.name.trim()) return errorAlert({ title: 'Campo requerido', text: 'El nombre es obligatorio' });
    setSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/themes`,
        { name: form.name, description: form.description, contentTypes: ['images', 'videos', 'texts'], permissions: 'publico' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setThemes(prev => [res.data, ...prev]);
      setShowModal(false);
      setForm({ name: '', description: '' });
      successAlert({ text: 'Temática creada correctamente', timer: 2000 });
    } catch (err) {
      errorAlert({ title: 'Error', text: err?.response?.data?.message || 'Error al crear temática' });
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ name: '', description: '' });
  };

  /* ── ABRIR MODAL EDITAR ── */
  const openEditModal = (t) => {
    setEditForm({ _id: t._id, name: t.name, description: t.description || '' });
    setEditModal(true);
  };

  const closeEditModal = () => {
    setEditModal(false);
    setEditForm({ _id: '', name: '', description: '' });
  };

  /* ── GUARDAR EDICIÓN ── */
  const handleEditTheme = async () => {
  if (!editForm.name.trim()) return errorAlert({ title: 'Campo requerido', text: 'El nombre es obligatorio' });
  setSaving(true);
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/admin/themes/${editForm._id}`,
      { name: editForm.name, description: editForm.description },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setThemes(prev => prev.map(t => (t._id === editForm._id ? { ...t, ...res.data } : t)));
    closeEditModal();
    successAlert({ text: 'Temática actualizada correctamente', timer: 2000 });
  } catch (err) {
    errorAlert({ title: 'Error', text: err?.response?.data?.message || 'Error al editar temática' });
  } finally {
    setSaving(false);
  }
};

  /* ══════════════════════════ RENDER ══════════════════════════ */
  return (
    <MainContainer active={isDark}>

      {/* ── TOP BAR: botón a la izquierda, título a la derecha ── */}
      <TopBar>
        <Header active={isDark}>Temáticas</Header>
      </TopBar>

      {!isLoggedIn ? (
        <EmptyMessage active={isDark}>Debes iniciar sesión para ver las temáticas.</EmptyMessage>
      ) : (
        <>
          <SearchBarContainer>
              <CreateBtn onClick={() => setShowModal(true)}>
                <FaPlus /> Nueva Temática
              </CreateBtn>
            <InputWrapper>
              <SearchInput
                active={isDark}
                value={searchTerm}
                onChange={handleChange}
                placeholder="Buscar temática..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <IconButton active={isDark} onClick={() => handleSearch()}>
                <FaSearch />
              </IconButton>
            </InputWrapper>
          </SearchBarContainer>

          {loading ? (
              <LoadingContainer>
                <Spinner />
                <LoadingText active={isDark}>Cargando temáticas...</LoadingText>
              </LoadingContainer>
            ) : themes.length === 0 ? (
              <EmptyMessage active={isDark}>No se encontraron temáticas.</EmptyMessage>
            ) : (
              <ThemeGrid>
                {themes.map((t) => (
              <ThemeCard key={t._id} active={isDark} onClick={() => navigate(`/dashboard?theme=${t._id}`)}>
                <ThemeName active={isDark}>{t.name}</ThemeName>
                <ThemeDescription active={isDark}>{t.description}</ThemeDescription>
                <CardFooter active={isDark}>
                  <ThemeId active={isDark}>ID: {t._id.substring(0, 8)}...</ThemeId>
                  {role === 'Admin' && (
                    <ActionButtons>
                      <EditButton
                        active={isDark}
                        title="Editar"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(t);
                        }}
                      >
                        <FaEdit />
                      </EditButton>
                      <DeleteButton
                        active={isDark}
                        title="Eliminar"
                        onClick={(e) => {
                            e.stopPropagation(); 
                            handleDelete(t._id);
                          }}
                        disabled={deleting === t._id}
                      >
                        {deleting === t._id ? '...' : <FaTrash />}
                      </DeleteButton>
                    </ActionButtons>
                  )}
                </CardFooter>
              </ThemeCard>
            ))}
          </ThemeGrid>
          )}
        </>
      )}

      {/* ══════ MODAL CREAR ══════ */}
      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalBox active={isDark} onClick={e => e.stopPropagation()}>
            <ModalHeader active={isDark}>
              <ModalTitle active={isDark}>Nueva Temática</ModalTitle>
              <CloseBtn active={isDark} onClick={closeModal}><FaTimes /></CloseBtn>
            </ModalHeader>

            <ModalBody>
              <FieldGroup>
                <FieldLabel active={isDark}>Nombre *</FieldLabel>
                <FieldInput
                  active={isDark}
                  placeholder="Ej: Fotografía"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel active={isDark}>Descripción</FieldLabel>
                <FieldTextarea
                  active={isDark}
                  placeholder="Descripción de la temática..."
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  rows={3}
                />
              </FieldGroup>
            </ModalBody>

            <ModalFooter active={isDark}>
              <CancelBtn active={isDark} onClick={closeModal}>
                <FaTimes /> Cancelar
              </CancelBtn>
              <SaveBtn onClick={handleCreateTheme} disabled={saving}>
                <FaSave /> {saving ? 'Guardando...' : 'Crear Temática'}
              </SaveBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ══════ MODAL EDITAR ══════ */}
      {editModal && (
        <ModalOverlay onClick={closeEditModal}>
          <ModalBox active={isDark} onClick={e => e.stopPropagation()}>
            <ModalHeader active={isDark}>
              <ModalTitle active={isDark}>Editar Temática</ModalTitle>
              <CloseBtn active={isDark} onClick={closeEditModal}><FaTimes /></CloseBtn>
            </ModalHeader>

            <ModalBody>
              <FieldGroup>
                <FieldLabel active={isDark}>Nombre *</FieldLabel>
                <FieldInput
                  active={isDark}
                  placeholder="Ej: Fotografía"
                  value={editForm.name}
                  onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel active={isDark}>Descripción</FieldLabel>
                <FieldTextarea
                  active={isDark}
                  placeholder="Descripción de la temática..."
                  value={editForm.description}
                  onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                  rows={3}
                />
              </FieldGroup>
            </ModalBody>

            <ModalFooter active={isDark}>
              <CancelBtn active={isDark} onClick={closeEditModal}>
                <FaTimes /> Cancelar
              </CancelBtn>
              <SaveBtn onClick={handleEditTheme} disabled={saving}>
                <FaSave /> {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

    </MainContainer>
  );
};

/* ═══════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════ */

const MainContainer = styled.div`
  background-color: ${p => p.active ? '#000' : '#fff'};
  height: 100%;
  padding: 40px;
  transition: background-color 0.3s;
  @media(max-width:768px){ padding: 20px; }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 12px;
`;

const Header = styled.h2`
  margin: 0;
  font-weight: 700;
  font-size: 1.4rem;
  color: ${p => p.active ? '#fff' : '#111'};
`;

const CreateBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(79,70,229,0.3);
  &:hover { opacity: 0.88; transform: translateY(-1px); }
`;

const SearchBarContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 400px;
  display: flex;
  align-items: center;
  @media(max-width:768px){ width: 90%; }
`;

const SearchInput = styled.input`
  width: 100%;
  background: ${p => p.active ? '#1a1a1a' : '#f7f8fa'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#ddd'};
  border-radius: 25px;
  padding: 12px 50px 12px 20px;
  font-size: 15px;
  color: ${p => p.active ? '#e0e0e0' : '#333'};
  outline: none;
  transition: all 0.2s;
  &::placeholder { color: ${p => p.active ? '#555' : '#bbb'}; }
  &:focus { border-color: ${p => p.active ? '#444' : '#4f46e5'}; }
`;

const IconButton = styled.button`
  position: absolute;
  right: 15px;
  background: none;
  border: none;
  color: ${p => p.active ? '#555' : '#bbb'};
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  &:hover { color: ${p => p.active ? '#fff' : '#4f46e5'}; }
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: ${p => p.active ? '#555' : '#bbb'};
  font-size: 15px;
`;

const ThemeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const ThemeCard = styled.div`
  background: ${p => p.active ? '#141414' : '#fff'};
  border: 1px solid ${p => p.active ? '#222' : '#e8e8e8'};
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 2px 10px ${p => p.active ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.06)'};
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px ${p => p.active ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.12)'};
  }
`;

const ThemeName        = styled.h3`margin:0 0 8px;font-size:1rem;font-weight:700;color:${p=>p.active?'#fff':'#111'};`;
const ThemeDescription = styled.p`color:${p=>p.active?'#888':'#666'};font-size:0.88rem;line-height:1.5;margin-bottom:16px;`;
const CardFooter       = styled.div`display:flex;justify-content:space-between;align-items:center;border-top:1px solid ${p=>p.active?'#222':'#f0f0f0'};padding-top:12px;`;
const ThemeId          = styled.span`font-size:11px;color:${p=>p.active?'#444':'#ccc'};`;
const ActionButtons    = styled.div`display:flex;gap:8px;`;

const EditButton = styled.button`
  background:${p=>p.active?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.03)'};
  border:1px solid ${p=>p.active?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)'};
  color:${p=>p.active?'#aaa':'#666'};
  width:32px;height:32px;border-radius:8px;font-size:13px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;transition:all 0.2s;
  &:hover{background:rgba(255,193,7,0.15);border-color:#ffc107;color:#ffc107;transform:scale(1.1);}
`;

const DeleteButton = styled.button`
  background:${p=>p.active?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.03)'};
  border:1px solid ${p=>p.active?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)'};
  color:${p=>p.active?'#aaa':'#666'};
  width:32px;height:32px;border-radius:8px;font-size:13px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;transition:all 0.2s;
  &:hover:not(:disabled){background:rgba(220,53,69,0.15);border-color:#dc3545;color:#dc3545;transform:scale(1.1);}
  &:disabled{opacity:0.5;cursor:not-allowed;}
`;

/* ── Modal ── */
const ModalOverlay = styled.div`
  position:fixed;inset:0;
  background:rgba(0,0,0,0.6);
  display:flex;align-items:center;justify-content:center;
  z-index:2000;
  backdrop-filter:blur(4px);
  animation:fadeIn 0.15s ease;
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
`;

const ModalBox = styled.div`
  background:${p=>p.active?'#141414':'#fff'};
  border:1px solid ${p=>p.active?'#222':'#e8e8e8'};
  border-radius:16px;
  width:100%;max-width:500px;
  box-shadow:0 20px 60px rgba(0,0,0,0.4);
  animation:slideUp 0.2s ease;
  @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
  @media(max-width:540px){margin:0 16px;}
`;

const ModalHeader = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:20px 24px 16px;
  border-bottom:1px solid ${p=>p.active?'#222':'#f0f0f0'};
`;

const ModalTitle = styled.h3`
  margin:0;font-size:1.1rem;font-weight:700;
  color:${p=>p.active?'#fff':'#111'};
`;

const CloseBtn = styled.button`
  background:${p=>p.active?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.04)'};
  border:1px solid ${p=>p.active?'#2a2a2a':'#eee'};
  color:${p=>p.active?'#888':'#999'};
  width:32px;height:32px;border-radius:8px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;font-size:13px;
  &:hover{color:#dc3545;border-color:rgba(220,53,69,0.4);}
`;

const ModalBody = styled.div`
  padding:20px 24px;
  display:flex;flex-direction:column;gap:16px;
`;

const ModalFooter = styled.div`
  display:flex;justify-content:flex-end;gap:10px;
  padding:16px 24px 20px;
  border-top:1px solid ${p=>p.active?'#222':'#f0f0f0'};
`;

const FieldGroup   = styled.div`display:flex;flex-direction:column;gap:6px;`;
const FieldLabel   = styled.label`font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:${p=>p.active?'#555':'#999'};`;

const FieldInput = styled.input`
  background:${p=>p.active?'#1e1e1e':'#f7f8fa'};
  border:1px solid ${p=>p.active?'#2a2a2a':'#e0e0e0'};
  border-radius:8px;padding:10px 14px;font-size:14px;
  color:${p=>p.active?'#e0e0e0':'#222'};outline:none;
  width:100%;box-sizing:border-box;
  &:focus{border-color:rgba(79,70,229,0.6);}
`;

const FieldTextarea = styled.textarea`
  background:${p=>p.active?'#1e1e1e':'#f7f8fa'};
  border:1px solid ${p=>p.active?'#2a2a2a':'#e0e0e0'};
  border-radius:8px;padding:10px 14px;font-size:14px;
  color:${p=>p.active?'#e0e0e0':'#222'};outline:none;
  width:100%;box-sizing:border-box;resize:vertical;font-family:inherit;
  &:focus{border-color:rgba(79,70,229,0.6);}
`;

const CancelBtn = styled.button`
  display:flex;align-items:center;gap:6px;
  padding:9px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;
  background:${p=>p.active?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.04)'};
  border:1px solid ${p=>p.active?'#2a2a2a':'#e0e0e0'};
  color:${p=>p.active?'#888':'#666'};
  &:hover{opacity:0.8;}
`;

const SaveBtn = styled.button`
  display:flex;align-items:center;gap:6px;
  padding:9px 20px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;
  background:linear-gradient(135deg,#4f46e5,#7c3aed);
  border:none;color:#fff;
  box-shadow:0 4px 12px rgba(79,70,229,0.3);
  transition:all 0.2s;
  &:hover:not(:disabled){opacity:0.88;transform:translateY(-1px);}
  &:disabled{opacity:0.5;cursor:not-allowed;}
`;

// 4. Agrega estos styled components al final
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 44px;
  height: 44px;
  border: 4px solid rgba(79, 70, 229, 0.2);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${p => p.active ? '#666' : '#999'};
  font-size: 14px;
  font-weight: 500;
  margin: 0;
`;

export default ThemeSearcher;